import { useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Instagram } from "lucide-react";
import Subscribers from "./pages/Subscribers";
import "./App.css";

const LOGO_URL =
  "https://customer-assets-0z36b82j.emergentagent.net/job_1d9e702b-0851-4c32-a2ce-cc18554297c8/artifacts/dm25obz8_KC.webp";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INSTAGRAM_URL = "https://instagram.com/k.atrinacatrina";

const STARS = [
  { x: 15.5, y: 4.8, delay: "0s" },
  { x: 63.5, y: 33.2, delay: "0.7s" },
  { x: 14.9, y: 73.2, delay: "1.4s" },
  { x: 63.6, y: 73.2, delay: "2.1s" },
];

function Home() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || status === "loading") return;
    setStatus("loading");
    try {
      const res = await fetch(`${API}/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.ok) {
        setStatus("success");
      } else if (res.status === 409) {
        setStatus("duplicate");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const done = status === "success" || status === "duplicate";

  return (
    <main
      data-testid="landing-page"
      className="min-h-screen w-full bg-white flex flex-col items-center justify-center px-6 py-10"
    >
      <div className="kc-fade-in flex flex-col items-center">
        <div className="kc-logo-wrap">
          <img
            data-testid="centered-image"
            src={LOGO_URL}
            alt="KC monogram logo"
            className="max-w-full w-auto h-auto object-contain max-h-[50vh] md:max-h-[54vh] block"
          />
          {STARS.map((s, i) => (
            <div
              key={i}
              data-testid={`twinkle-star-${i + 1}`}
              className="kc-star"
              style={{ left: `${s.x}%`, top: `${s.y}%`, animationDelay: s.delay }}
            >
              <svg viewBox="0 0 24 24" className="kc-star-shape" aria-hidden="true">
                <path
                  d="M12 1.8 15 8.5 22.2 9.2 16.9 14 18.4 21.2 12 17.3 5.6 21.2 7.1 14 1.8 9.2 9 8.5Z"
                  fill="#000000"
                />
              </svg>
            </div>
          ))}
        </div>
        <p
          data-testid="brand-tagline"
          className="kc-tagline mt-8 md:mt-10 text-lg md:text-2xl tracking-[0.28em] md:tracking-[0.35em] uppercase text-neutral-900 text-center whitespace-nowrap"
        >
          Katrina-Catrina
        </p>
        <p
          data-testid="established-text"
          className="kc-established mt-3 text-xs md:text-sm tracking-[0.3em] uppercase text-neutral-500 text-center"
        >
          Established 2026
        </p>

        {done ? (
          <p
            data-testid="subscribe-success"
            className="kc-form-text mt-10 text-xs md:text-sm tracking-[0.25em] uppercase text-neutral-700 text-center"
          >
            {status === "duplicate"
              ? "You're already on the list"
              : "You're on the list — we'll be in touch"}
          </p>
        ) : (
          <form
            data-testid="subscribe-form"
            onSubmit={subscribe}
            className="mt-10 w-64 sm:w-72 flex items-end gap-4"
          >
            <input
              data-testid="subscribe-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email for launch news"
              className="kc-form-text flex-1 min-w-0 bg-transparent border-0 border-b border-neutral-300 focus:border-neutral-900 outline-none py-2 text-sm tracking-[0.12em] text-neutral-900 placeholder:text-neutral-400 transition-colors"
            />
            <button
              data-testid="subscribe-submit-button"
              type="submit"
              disabled={status === "loading"}
              className="kc-form-text shrink-0 text-xs tracking-[0.3em] uppercase text-neutral-900 border-b border-neutral-900 py-2 hover:opacity-50 transition-opacity disabled:opacity-40"
            >
              {status === "loading" ? "Sending" : "Notify me"}
            </button>
          </form>
        )}
        {status === "error" && (
          <p
            data-testid="subscribe-error"
            className="kc-form-text mt-3 text-xs tracking-[0.15em] uppercase text-red-800"
          >
            Something went wrong — please try again
          </p>
        )}

        <a
          data-testid="instagram-link"
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Katrina-Catrina on Instagram"
          className="mt-10 text-neutral-400 hover:text-neutral-900 transition-colors"
        >
          <Instagram size={18} strokeWidth={1.25} />
        </a>
      </div>
    </main>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/subscribers" element={<Subscribers />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
